import { createFileRoute, redirect } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import z from 'zod';

import { postSignInRedirectStorageKey } from '@constants/auth.constant';
import {
  resolvePostSignInRedirect,
  retrySignInAfterLinking,
} from '@helpers/ssoSignIn.helper';

const redirectingSearchSchema = z.looseObject({
  error: z.string().optional(),
  error_description: z.string().optional(),
});

export const Route = createFileRoute('/redirecting')({
  validateSearch: search => redirectingSearchSchema.parse(search),
  beforeLoad: async ({ context, search }) => {
    if (search.error) {
      if (await retrySignInAfterLinking(search.error_description ?? '')) {
        return;
      }

      throw unauthorizedRedirect({
        error: search.error,
        errorDescription: search.error_description,
      });
    }

    if (!context.auth.isAuthenticated) {
      throw unauthorizedRedirect({});
    }

    throw successRedirect();
  },
  component: Redirecting,
});

function Redirecting() {
  return (
    <main className="grid min-h-dvh place-items-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </main>
  );
}

function successRedirect() {
  const target = resolvePostSignInRedirect({
    redirect: globalThis.sessionStorage.getItem(postSignInRedirectStorageKey),
  });

  globalThis.sessionStorage.removeItem(postSignInRedirectStorageKey);

  return redirect({ href: target, replace: true });
}

type UnauthorizedRedirectInput = {
  error?: string;
  errorDescription?: string;
};

function unauthorizedRedirect({
  error,
  errorDescription,
}: UnauthorizedRedirectInput) {
  globalThis.sessionStorage.removeItem(postSignInRedirectStorageKey);

  return redirect({
    replace: true,
    search: {
      error,
      error_description: errorDescription,
      reason: 'google-login-failed',
    },
    to: '/unauthorized',
  });
}
